<script lang="ts">
export default { name: 'DiffView' };
</script>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { MergeView } from '@codemirror/merge';
import { EditorState } from '@codemirror/state';
import { EditorView, lineNumbers } from '@codemirror/view';
import { json } from '@codemirror/lang-json';

const props = defineProps<{
  original: string;
  modified: string;
  language?: 'json' | 'plain';
}>();

const container = ref<HTMLElement | null>(null);
let view: MergeView | null = null;
let detachScrollSync: (() => void) | null = null;

const buildExtensions = () => {
  const exts = [EditorView.editable.of(false), EditorState.readOnly.of(true), lineNumbers()];
  if (props.language === 'json') exts.push(json());
  return exts;
};

const wireScrollSync = () => {
  if (!view?.a || !view?.b) return;
  const scrollA = view.a.scrollDOM;
  const scrollB = view.b.scrollDOM;
  let syncing = false;

  const onScrollA = () => {
    if (syncing) return;
    syncing = true;
    scrollB.scrollLeft = scrollA.scrollLeft;
    syncing = false;
  };
  const onScrollB = () => {
    if (syncing) return;
    syncing = true;
    scrollA.scrollLeft = scrollB.scrollLeft;
    syncing = false;
  };

  scrollA.addEventListener('scroll', onScrollA, { passive: true });
  scrollB.addEventListener('scroll', onScrollB, { passive: true });

  detachScrollSync = () => {
    scrollA.removeEventListener('scroll', onScrollA);
    scrollB.removeEventListener('scroll', onScrollB);
  };
};

const mount = () => {
  if (!container.value) return;
  view = new MergeView({
    a: { doc: props.original, extensions: buildExtensions() },
    b: { doc: props.modified, extensions: buildExtensions() },
    parent: container.value,
    highlightChanges: true,
    gutter: true,
    collapseUnchanged: { margin: 3, minSize: 4 },
  });
  wireScrollSync();
};

const destroy = () => {
  detachScrollSync?.();
  detachScrollSync = null;
  view?.destroy();
  view = null;
};

onMounted(mount);
onBeforeUnmount(destroy);

// Rebuild when inputs change (rare — typically the modal remounts per open).
watch(
  () => [props.original, props.modified, props.language],
  () => {
    destroy();
    mount();
  },
);
</script>

<template>
  <div
    ref="container"
    class="diff-view h-full w-full overflow-auto" />
</template>

<style>
.diff-view .cm-scroller {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 12px;
  overflow-x: auto !important;
}

/* Hatched placeholder for alignment gaps so absent content reads as intentional. */
.diff-view .cm-mergeSpacer {
  background-image: repeating-linear-gradient(
    135deg,
    rgba(100, 116, 139, 0.08) 0,
    rgba(100, 116, 139, 0.08) 1px,
    transparent 1px,
    transparent 8px
  );
  border-top: 1px dashed rgba(100, 116, 139, 0.25);
  border-bottom: 1px dashed rgba(100, 116, 139, 0.25);
}

/* Line-level backgrounds already signal changes; drop the extra underlines. */
.diff-view .cm-merge-a .cm-changedText,
.diff-view .cm-merge-b .cm-changedText {
  background: transparent;
}
</style>
